import React, { memo } from 'react';
import { BaseItem, CellProps } from '@lib/components/Table/types';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import TextCellWithIcon from '@lib/components/Table/commonComponents/TextCell/TextCellWithIcon';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { useTranslation } from 'react-i18next';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import styles from './UserSetupCell.module.scss';

interface Props {
  isViewOnly?: boolean;
  onResendEmailConfirmationHandler: () => void;
  onResendRegistrationConfirmationHandler: () => void;
  onResendAccountLockedHandler?: () => void;
}

function UserSetupCell<T extends BaseItem>(props: CellProps<T> & Props) {
  const {
    currentUserId,
    isViewOnly = false,
    item,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    onResendAccountLockedHandler,
  } = props;
  const {
    id,
    passwordSetupRequired,
    unconfirmedEmail,
    verified,
    deactivatedAt,
    email,
    lockedAt,
  } = item;
  const { t } = useTranslation();

  if (id === currentUserId) return null;

  // Account is locked - show warning and resend button
  if (lockedAt) {
    return (
      <div className={styles.root}>
        <TextCellWithIcon
          {...props}
          disabled
          value={t('account-suspected')}
          icon={
            <Chip
              leftIcon="warning"
              type={ChipTypes.chipOrange}
              size={ChipSizes.small}
            />
          }
          iconPosition="right"
        />
        {!isViewOnly && onResendAccountLockedHandler ? (
          <Button
            buttonText={t('send-recovery-email')}
            buttonType={ButtonTypes.primaryOutlined}
            onClick={onResendAccountLockedHandler}
          />
        ) : null}
      </div>
    );
  }

  if (deactivatedAt) {
    return (
      <div className={styles.root}>
        <Chip
          type={ChipTypes.chipDisabled}
          label={t('deactivated')}
          leftIconSymbolsOutlined
          leftIcon="do_not_disturb_on"
        />
      </div>
    );
  }

  // User needs password setup AND not verified = invitation pending
  if (passwordSetupRequired && !verified) {
    return (
      <div className={styles.root}>
        <TextCell {...props} disabled value={t('invitation-pending')} />
        {!isViewOnly && email ? (
          <Button
            buttonText={t('resend-invitation')}
            buttonType={ButtonTypes.primaryOutlined}
            onClick={onResendRegistrationConfirmationHandler}
          />
        ) : null}
      </div>
    );
  }

  // User needs password setup but is verified = finish registration pending
  if (passwordSetupRequired) {
    return (
      <div className={styles.root}>
        <TextCell
          {...props}
          disabled
          value={t('finish-registration-pending')}
        />
        {!isViewOnly && email ? (
          <Button
            buttonText={t('resend-invitation')}
            buttonType={ButtonTypes.primaryOutlined}
            onClick={onResendRegistrationConfirmationHandler}
          />
        ) : null}
      </div>
    );
  }

  // User has unconfirmed email change pending
  if (unconfirmedEmail) {
    return (
      <div className={styles.root}>
        <Button
          buttonText={t('resend-email-confirmation')}
          buttonType={ButtonTypes.primaryOutlined}
          onClick={onResendEmailConfirmationHandler}
        />
      </div>
    );
  }

  return null;
}

export default memo(UserSetupCell) as <T extends BaseItem>(
  props: CellProps<T> & Props,
) => React.ReactElement;
